/**
 * PmsIssues — Incidencias / Maintenance.
 *
 * Consume el `pmsRuntimeStore` compartido. Cualquier incidencia creada
 * desde Housekeeping aparece aquí en el instante en el que el usuario pulsa
 * "Crear incidencia", sin recargar la página.
 *
 * Tabs: Abiertas · En progreso · Resueltas.
 * Acciones locales: Asignar (self-assign al reporter demo), Empezar,
 * Resolver, Añadir nota. Todas mutan el store compartido.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Section } from "@/pages/Shell";
import PmsShell from "@/pages/admin/pms/PmsShell";
import {
  User as UserIcon, Camera, ChatCircle, CheckCircle, Play, Prohibit,
  ArrowRight, Sparkle,
} from "@phosphor-icons/react";
import { getVilla, getReservation } from "@/lib/pmsSelectors";
import {
  usePmsIssues, updateIssue, addIssueNote, DEMO_REPORTER,
} from "@/lib/pmsRuntimeStore";

const TAB_LABEL = {
  open_or_assigned: "Abiertas",
  in_progress:      "En progreso",
  resolved:         "Resueltas",
};

const CATEGORY_LABEL = {
  maintenance:   "Mantenimiento",
  cleaning:      "Limpieza",
  guest:         "Huésped",
  damage:        "Daño",
  missing_item:  "Falta material",
  other:         "Otros",
};

const PRIORITY_STYLE = {
  urgent: "bg-red-200 text-red-900 dark:bg-red-950/60 dark:text-red-100",
  high:   "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-200",
  medium: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
  low:    "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300",
};

const STATUS_LABEL = {
  open:        "Abierta",
  assigned:    "Asignada",
  in_progress: "En progreso",
  resolved:    "Resuelta",
};

const NEXT_ACTION = {
  open:        { label: "Asignar",  icon: UserIcon,     next: "assigned" },
  assigned:    { label: "Empezar",  icon: Play,         next: "in_progress" },
  in_progress: { label: "Resolver", icon: CheckCircle,  next: "resolved" },
  resolved:    null,
};

export default function PmsIssues() {
  const [tab, setTab] = useState("open_or_assigned");
  const allIssues = usePmsIssues();

  const filtered = useMemo(() => {
    return allIssues.filter((i) => {
      if (tab === "open_or_assigned") return i.status === "open" || i.status === "assigned";
      return i.status === tab;
    });
  }, [tab, allIssues]);

  const counts = useMemo(() => {
    const out = { open_or_assigned: 0, in_progress: 0, resolved: 0 };
    for (const i of allIssues) {
      if (i.status === "open" || i.status === "assigned") out.open_or_assigned++;
      else if (i.status === "in_progress") out.in_progress++;
      else if (i.status === "resolved") out.resolved++;
    }
    return out;
  }, [allIssues]);

  const advance = (issue, next) => {
    const patch = { status: next };
    // Self-assign al reporter demo cuando la acción es "Asignar".
    if (next === "assigned" && !issue.assignee) {
      patch.assignee = DEMO_REPORTER;
    }
    updateIssue(issue.issue_id, patch);
  };

  const addNote = (issueId) => addIssueNote(issueId, "Nota demo · sin persistencia");

  return (
    <PmsShell>
      <Section
        title="Incidencias · Mantenimiento"
        subtitle="Estado compartido con Housekeeping. Acciones locales sin backend."
      >
        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="pms-issues-tabs">
          {Object.entries(TAB_LABEL).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`pms-issues-tab-${key}`}
              className={`text-xs px-3 py-1.5 rounded-full ${
                tab === key
                  ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
                  : "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-700"
              }`}
            >
              {label}
              <span className="ml-1.5 tabular-nums opacity-70">{counts[key] || 0}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="dv-card text-sm text-stone-500 dark:text-stone-400 text-center py-10">
            Sin incidencias en esta pestaña.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="pms-issues-list">
            {filtered.map((i) => (
              <IssueCard
                key={i.issue_id}
                issue={i}
                onAdvance={(next) => advance(i, next)}
                onAddNote={() => addNote(i.issue_id)}
              />
            ))}
          </div>
        )}
      </Section>
    </PmsShell>
  );
}

function IssueCard({ issue, onAdvance, onAddNote }) {
  const villa = getVilla(issue.villa_id);
  const linkedRes = issue.reservation_id ? getReservation(issue.reservation_id) : null;
  const nextAction = NEXT_ACTION[issue.status];
  const isSession = issue.origin === "created_in_session";

  return (
    <div className="dv-card" data-testid={`pms-issue-${issue.issue_id}`} data-status={issue.status}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-400 flex items-center gap-2 flex-wrap">
            <span>{issue.issue_id}</span>
            <span>·</span>
            <span>{CATEGORY_LABEL[issue.category] || issue.category}</span>
            {isSession && (
              <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200 normal-case tracking-normal">
                <Sparkle size={9} weight="fill" /> Nueva
              </span>
            )}
            {issue.origin === "seed" && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400 normal-case tracking-normal">
                Demo seed
              </span>
            )}
          </div>
          <div className="mt-0.5 font-medium text-stone-900 dark:text-stone-100">
            {issue.title}
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
            <span className="text-[10px] text-stone-400 mr-1">{villa?.villa_id}</span>
            {villa?.name}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${PRIORITY_STYLE[issue.priority] || PRIORITY_STYLE.medium}`}>
            {issue.priority}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-stone-500 dark:text-stone-400">
            {STATUS_LABEL[issue.status] || issue.status}
          </span>
        </div>
      </div>

      {issue.description && (
        <p className="mt-3 text-xs text-stone-700 dark:text-stone-300 whitespace-pre-line">
          {issue.description}
        </p>
      )}

      <div className="mt-3 grid grid-cols-2 gap-3 text-[11px] text-stone-600 dark:text-stone-400">
        <div>
          <div className="uppercase tracking-widest text-[9px] text-stone-400">Reportado por</div>
          <div>
            {issue.reporter?.name || "—"}
            {issue.reporter?.role && (
              <span className="text-stone-400"> · {issue.reporter.role}</span>
            )}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-[9px] text-stone-400">Asignado a</div>
          <div>
            {issue.assignee?.name || (
              <span className="text-stone-400 italic">Sin asignar</span>
            )}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-widest text-[9px] text-stone-400">Reportado</div>
          <div className="tabular-nums">{fmtWhen(issue.reported_at)}</div>
        </div>
        {issue.resolved_at && (
          <div>
            <div className="uppercase tracking-widest text-[9px] text-stone-400">Resuelto</div>
            <div className="tabular-nums">{fmtWhen(issue.resolved_at)}</div>
          </div>
        )}
      </div>

      {(issue.blocks_inventory || linkedRes || issue.linked_block_id) && (
        <div className="mt-3 space-y-1.5">
          {issue.blocks_inventory && (
            <div className="rounded border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 p-2 text-[11px]">
              <div className="inline-flex items-center gap-1 text-red-800 dark:text-red-200 font-medium">
                <Prohibit size={12} weight="fill" /> Bloquea disponibilidad
              </div>
              <div className="text-red-700 dark:text-red-300 mt-0.5">
                Fairness compensable:{" "}
                <span className="font-medium">
                  {issue.fairness_compensable ? "Sí" : "No"}
                </span>
              </div>
              {issue.linked_block_id && (
                <div className="text-red-600 dark:text-red-400 mt-0.5">
                  Bloqueo vinculado: <span className="font-mono">{issue.linked_block_id}</span>
                </div>
              )}
            </div>
          )}
          {linkedRes && (
            <div className="text-[11px]">
              <Link
                to={`/pms/reservations/${linkedRes.reservation_id}`}
                className="inline-flex items-center gap-1 text-stone-700 dark:text-stone-300 hover:underline"
              >
                Reserva relacionada: <span className="font-mono">{linkedRes.reservation_id}</span>
                <ArrowRight size={10} />
              </Link>
            </div>
          )}
        </div>
      )}

      {issue.photos_placeholder > 0 && (
        <div className="mt-3 flex gap-1 flex-wrap">
          {Array.from({ length: issue.photos_placeholder }).map((_, i) => (
            <div
              key={i}
              className="w-12 h-12 rounded border border-dashed border-stone-300 dark:border-stone-700 flex items-center justify-center text-stone-400"
              title="Placeholder de foto — sin backend en demo"
            >
              <Camera size={14} />
            </div>
          ))}
        </div>
      )}

      {Array.isArray(issue.extra_notes) && issue.extra_notes.length > 0 && (
        <div className="mt-3 border-t border-stone-100 dark:border-stone-900 pt-2 text-[11px] text-stone-600 dark:text-stone-400">
          <div className="uppercase tracking-widest text-[9px] text-stone-400 mb-1">Notas</div>
          <ul className="space-y-0.5">
            {issue.extra_notes.map((n, i) => (
              <li key={i}>· {n.text} <span className="text-stone-400">({fmtTime(n.at)})</span></li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {nextAction && (
          <ActionBtn onClick={() => onAdvance(nextAction.next)} icon={nextAction.icon} primary>
            {nextAction.label}
          </ActionBtn>
        )}
        <ActionBtn onClick={onAddNote} icon={ChatCircle}>Añadir nota</ActionBtn>
        <ActionBtn onClick={() => window.alert("Photo upload no disponible en demo.")} icon={Camera}>Añadir foto</ActionBtn>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, primary, children }) {
  const cls = primary
    ? "bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 hover:opacity-90"
    : "border border-stone-300 dark:border-stone-700 text-stone-700 dark:text-stone-200 hover:border-stone-500";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded inline-flex items-center gap-1.5 min-h-[36px] ${cls}`}
    >
      {Icon && <Icon size={12} weight="bold" />}
      {children}
    </button>
  );
}

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-ES", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}
function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}
